import { useCallback, useEffect } from 'react';
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';

import { QueryState } from 'app/components/Table/core/types';

import { stringifyObject } from 'app/helpers/utils';
import { camelCase, isEmpty, omit, omitBy } from 'lodash';

import { useQueryParamStore } from './store';
import { parse, stringify } from 'qs';
import { useOrganizationStore } from 'theme/layout/components/aside/store';

export const useSyncQueryToState = <T = any>(
  locationPath: string,
  defaultParams?: Partial<QueryState> & T,
  options?: {
    skipFields?: string[];
    requiredFields?: string[];
  },
) => {
  const clearState = useQueryParamStore((s) => s.clearState);

  const pathname = camelCase(locationPath);

  const location = useLocation();

  const [searchParams, setSearchParams] = useSearchParams();

  const paramState = searchParams.toString();

  useEffect(() => {
    if (options?.skipFields) {
      useQueryParamStore.getState().setState({ skipFields: options.skipFields });
    }
    return clearState;
  }, []);

  useEffect(() => {
    const synced = useQueryParamStore.getState().state.synced;
    const intial = !checkParamSynced(synced) && defaultParams && !paramState;
    const newParams: any = intial ? defaultParams : parse(paramState);

    if (intial) {
      setSearchParams(stringify(defaultParams), { replace: true, state: location.state });
    }

    if (isEmpty(newParams)) {
      return;
    }

    useQueryParamStore.getState().setState({
      curParams: stringifyObject(newParams),
      synced: {
        ...synced,
        [pathname]: true,
      },
    });
  }, [paramState, location]);

  return null;
};

export const useGetQueryParams = <T = any>() => {
  const curParams = useQueryParamStore((s) => s.state.curParams);
  const synced = useQueryParamStore((s) => s.state.synced);
  const skipFields = useQueryParamStore((s) => s.state.skipFields);
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  return { curParams: curParams as QueryState & T, synced, skipFields };
};

export const useChangeParams = <T = any>() => {
  const location = useLocation();
  const { orgId } = useParams();
  const organization = useOrganizationStore((state) => state.state.organization);
  const [_, setSearchParams] = useSearchParams();
  const navigate = useNavigate();

  const onChangeParams = useCallback(
    (
      newParams: Partial<QueryState> & T,
      options?: {
        removeParams?: string | string[];
        navigateOptions?: {
          replace?: boolean;
          state?: any;
        };
      },
    ) => {
      const combinedParams = { ...useQueryParamStore.getState().state.curParams, ...newParams };
      const queryString = stringify(
        omitBy(
          options?.removeParams ? omit(combinedParams, options.removeParams) : combinedParams,
          (v) => v === '' || v === undefined || v === null,
        ),
      );

      // If we're on an org-prefixed path, use navigate instead of setSearchParams
      // to ensure the org prefix is preserved
      const currentOrgId = orgId || organization?.id;
      if (currentOrgId && location.pathname.includes(`/org/${currentOrgId}`)) {
        navigate(
          {
            pathname: location.pathname,
            search: queryString,
          },
          {
            ...options?.navigateOptions,
            state: { ...options?.navigateOptions?.state, ...(location.state as any) },
          },
        );
        return;
      }

      setSearchParams(queryString, {
        ...options?.navigateOptions,
        state: { ...options?.navigateOptions?.state, ...(location.state as any) },
      });
    },
    [location, orgId, organization],
  );

  const removeParams = useCallback(
    (param: string | string[]) => {
      const newParams = omit(useQueryParamStore.getState().state.curParams, param);
      const queryString = stringify(newParams);

      // If we're on an org-prefixed path, use navigate instead of setSearchParams
      const currentOrgId = orgId || organization?.id;
      if (currentOrgId && location.pathname.includes(`/org/${currentOrgId}`)) {
        navigate(
          {
            pathname: location.pathname,
            search: queryString,
          },
          { state: location.state },
        );
        return;
      }

      setSearchParams(queryString, { state: location.state });
    },
    [location, orgId, organization],
  );

  return {
    onChangeParams,
    removeParams,
  };
};

export const checkParamSynced = (synced: Record<string, boolean>) => {
  return synced[camelCase(window.location.pathname)] ? true : false;
};
